import { faArrowUp } from '@fortawesome/pro-light-svg-icons/faArrowUp';
import { faCircleStop } from '@fortawesome/pro-light-svg-icons/faCircleStop';
import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { QBox, QButton, QIcon, QTextField } from '@captivateiq/quotient';

import { useAIShellContext } from '@captivateiq/assistant/AIShellContext';

import { expandedTextColumnSx } from '../../shared/expandedTextColumnSx';

type MessageInputProps = {
  onSubmit: (message: string) => Promise<void>;
  agentThinking?: boolean;
};

const MAX_TEXTAREA_ROWS = 5;

export const MessageInput = ({ onSubmit, agentThinking = false }: MessageInputProps) => {
  const { t } = useTranslation('assistant');
  const { isExpanded } = useAIShellContext();

  const [content, setContent] = useState<string>('');
  const textRef = useRef<HTMLTextAreaElement>(null);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      setContent(event.target.value);
    },
    [setContent],
  );

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      if (agentThinking || content.trim() === '') {
        return;
      }
      onSubmit(content);
      setContent('');
    },
    [onSubmit, setContent, content, agentThinking],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' && !event.shiftKey && !agentThinking && content.trim() !== '') {
        event.preventDefault();
        handleSubmit(event);
      }
    },
    [handleSubmit, agentThinking, content],
  );

  return (
    <QBox sx={expandedTextColumnSx(isExpanded)}>
      <form onSubmit={handleSubmit}>
        <QBox
          sx={{
            position: 'relative',
            border: '1px solid',
            borderColor: 'grey.200',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'flex-end',
          }}
        >
          <QTextField
            data-testid="ai-message-input"
            inputRef={textRef}
            maxRows={MAX_TEXTAREA_ROWS}
            multiline
            placeholder={t('input_placeholders.message') as string}
            sx={{
              flex: 1,
              padding: 0,
            }}
            value={content}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
          />
          <QBox
            sx={{
              position: 'absolute',
              right: '4px',
              bottom: '4px',
              display: 'flex',
              gap: '4px',
            }}
          >
            <QButton
              data-testid="ai-send-button"
              size="small"
              sx={{
                minWidth: 'unset',
                padding: '4px',
                borderRadius: '4px',
              }}
              type="submit"
              variant={content && !agentThinking ? 'contained' : 'outlined'}
            >
              <QIcon icon={agentThinking ? faCircleStop : faArrowUp} sx={{ fontSize: '16px' }} />
            </QButton>
          </QBox>
        </QBox>
      </form>
    </QBox>
  );
};
